import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingDown, TrendingUp, Minus, Users, Building2, Zap, Sparkles, Send, MessageSquare, ChevronDown, Share2, Mail, FileText, CheckCircle2, Target } from 'lucide-react';
import { Toast } from './Toast';
import { useKPIs } from '../contexts/KPIContext';
import { useEscapeKey } from '../hooks/useEscapeKey';
export interface Insight {
  id: string;
  title: string;
  description: string;
  impactScore: number; // negative means risk reduction
  effortLevel: 'Low' | 'Medium' | 'High';
  actionValue: 'Top Priority' | 'Strategic' | 'Quick Win' | 'Deprioritize';
  affectedSites: number;
  affectedWorkers: number;
  trend: 'worsening' | 'stable' | 'improving';
  suggestedAction: string;
  category: 'ptp' | 'weather' | 'contractor' | 'inspection' | 'positive';
  supportingData: string[];
}
interface InsightDetailModalProps {
  insight: Insight | null;
  onClose: () => void;
}
interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}
export function InsightDetailModal({
  insight,
  onClose
}: InsightDetailModalProps) {
  useEscapeKey(onClose, !!insight);
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const {
    addKPIFromInsight
  } = useKPIs();
  useEffect(() => {
    if (insight && messages.length === 0) {
      // Initial AI greeting
      setMessages([{
        id: '1',
        role: 'assistant',
        text: `I identified that ${insight.title.toLowerCase()}. This correlates with higher incident rates. What would you like to explore?`
      }]);
    }
  }, [insight]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  if (!insight) return null;
  const getTrendIcon = (trend: Insight['trend']) => {
    switch (trend) {
      case 'worsening':
        return <TrendingUp size={16} className="text-red-500" data-id="element-617" />;
      case 'improving':
        return <TrendingDown size={16} className="text-emerald-500" data-id="element-618" />;
      case 'stable':
        return <Minus size={16} className="text-gray-500" data-id="element-619" />;
    }
  };
  const getActionValueColor = (value: Insight['actionValue']) => {
    switch (value) {
      case 'Top Priority':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'Quick Win':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Strategic':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Deprioritize':
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const quickPrompts = ['Why is this happening?', 'Who is most affected?', 'What is the cost of inaction?', 'Draft an action plan'];
  const generateMockResponse = (userQuery: string): string => {
    const lowerQuery = userQuery.toLowerCase();
    if (lowerQuery.includes('why')) {
      return `Based on the data, there are three main factors driving this:\n1. Schedule compression leading to rushed procedures\n2. Recent influx of new workers (<30 days on site)\n3. Inconsistent enforcement across different shifts`;
    }
    if (lowerQuery.includes('who') || lowerQuery.includes('affected')) {
      return `This issue primarily affects SteelCo Inc and AV Mechanic LLC crews across ${insight.affectedSites} sites. The highest concentration is among workers with less than 6 months of experience.`;
    }
    if (lowerQuery.includes('cost') || lowerQuery.includes('inaction')) {
      return `Based on historical incident data, continuing this trend could lead to an estimated $18,000-$24,000 in monthly indirect costs (delays, rework, minor incidents). Addressing it now has a high ROI.`;
    }
    if (lowerQuery.includes('draft') || lowerQuery.includes('plan')) {
      return `Here is a draft action plan:\n\n**Phase 1 (Next 48h):**\n- Issue site-wide safety bulletin\n- Require supervisor sign-off on all related tasks\n\n**Phase 2 (Next Week):**\n- Conduct targeted toolbox talks\n- Review metrics to ensure compliance`;
    }
    return `I've analyzed the data across your projects. Implementing the suggested action ("${insight.suggestedAction}") is estimated to reduce overall risk by ${Math.abs(insight.impactScore)}%.`;
  };
  const handleSend = (text: string) => {
    if (!text.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      text
    };
    setMessages(prev => [...prev, newUserMsg]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const newAiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: generateMockResponse(text)
      };
      setMessages(prev => [...prev, newAiMsg]);
    }, 1500);
  };
  const handleActionClick = (action: string) => {
    setToastMessage(`${action} initiated`);
    setShowToast(true);
    setShowShareMenu(false);
  };
  return <AnimatePresence data-id="element-620">
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-621">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" data-id="element-622" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-623">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-624">
            <div className="flex items-center gap-3" data-id="element-625">
              <div className="p-2 bg-indigo-100 rounded-lg" data-id="element-626">
                <Sparkles size={20} className="text-indigo-600" data-id="element-627" />
              </div>
              <div data-id="element-628">
                <h2 className="text-lg font-bold text-gray-900" data-id="element-629">
                  Company Insight
                </h2>
                <p className="text-sm text-gray-500" data-id="element-630">Strategic risk analysis</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-631">
              <div className="relative" ref={shareMenuRef} data-id="element-632">
                <button onClick={() => setShowShareMenu(!showShareMenu)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-633">
                  <Share2 size={16} data-id="element-634" />
                  Take Action
                  <ChevronDown size={14} data-id="element-635" />
                </button>

                <AnimatePresence data-id="element-636">
                  {showShareMenu && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-10" data-id="element-637">
                      <button onClick={() => handleActionClick('Drafting email')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-638">
                        <Mail size={16} className="text-gray-400" data-id="element-639" />
                        Draft email to team
                      </button>
                      <button onClick={() => handleActionClick('Creating policy memo')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-640">
                        <FileText size={16} className="text-gray-400" data-id="element-641" />
                        Create policy memo
                      </button>
                      <button onClick={() => handleActionClick('Generating report')} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left" data-id="element-642">
                        <CheckCircle2 size={16} className="text-gray-400" data-id="element-643" />
                        Assign to safety team
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-644">
                <X size={20} data-id="element-645" />
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row flex-1 overflow-hidden" data-id="element-646">
            {/* Left Column: Insight Details */}
            <div className="w-full md:w-1/2 border-r border-gray-200 overflow-y-auto p-6 bg-white" data-id="element-647">
              <div className="mb-6" data-id="element-648">
                <div className="flex items-center gap-2 mb-3" data-id="element-649">
                  <span className={`px-2.5 py-1 text-xs font-bold rounded-full border ${getActionValueColor(insight.actionValue)}`} data-id="element-650">
                    {insight.actionValue}
                  </span>
                  <span className="px-2.5 py-1 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full border border-gray-200" data-id="element-651">
                    Effort: {insight.effortLevel}
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-652">
                  {insight.title}
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-653">{insight.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-654">
                  <div className={`p-4 rounded-xl border ${insight.impactScore > 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-200'}`} data-id="element-655">
                    <p className={`text-xs font-semibold mb-1 uppercase tracking-wider ${insight.impactScore > 0 ? 'text-emerald-800' : 'text-amber-800'}`} data-id="element-656">
                      {insight.impactScore > 0 ? 'Risk Improved' : 'Can Reduce Risk By'}
                    </p>
                    <div className="flex items-baseline gap-1" data-id="element-657">
                      <span className={`text-3xl font-bold ${insight.impactScore > 0 ? 'text-emerald-600' : 'text-amber-700'}`} data-id="element-658">
                        {insight.impactScore > 0 ? '+' : ''}
                        {Math.abs(insight.impactScore)}%
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-659">
                    <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider" data-id="element-660">
                      Trend
                    </p>
                    <div className="flex items-center gap-2" data-id="element-661">
                      {getTrendIcon(insight.trend)}
                      <span className="text-lg font-bold text-gray-900 capitalize" data-id="element-662">
                        {insight.trend}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6 mb-8 py-4 border-y border-gray-100" data-id="element-663">
                  <div className="flex items-center gap-2" data-id="element-664">
                    <Building2 size={18} className="text-gray-400" data-id="element-665" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-666">
                      {insight.affectedSites} Sites Affected
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-667">
                    <Users size={18} className="text-gray-400" data-id="element-668" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-669">
                      {insight.affectedWorkers} Workers
                    </span>
                  </div>
                </div>

                <div className="mb-6" data-id="element-670">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-671">
                    <Zap size={16} className="text-amber-500" data-id="element-672" />
                    Suggested Action
                  </h4>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-673">
                    <p className="text-sm font-medium text-amber-900 mb-4" data-id="element-674">
                      {insight.suggestedAction}
                    </p>
                    <button onClick={() => {
                    addKPIFromInsight(insight);
                    setToastMessage('Added to KPI tracker');
                    setShowToast(true);
                  }} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-amber-200 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors" data-id="element-675">
                      <Target size={14} data-id="element-676" />
                      Track as KPI
                    </button>
                  </div>
                </div>

                <div data-id="element-677">
                  <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-678">
                    Supporting Data
                  </h4>
                  <ul className="space-y-2" data-id="element-679">
                    {insight.supportingData.map((data, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-680">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" data-id="element-681" />
                        {data}
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Column: AI Chat */}
            <div className="w-full md:w-1/2 flex flex-col bg-gray-50" data-id="element-682">
              <div className="p-4 border-b border-gray-200 bg-white shadow-sm z-10" data-id="element-683">
                <div className="flex items-center gap-2" data-id="element-684">
                  <Sparkles size={18} className="text-indigo-600" data-id="element-685" />
                  <h3 className="font-bold text-gray-900" data-id="element-686">
                    Explore with Bilton AI
                  </h3>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-687">
                  Ask questions to dig deeper into this insight.
                </p>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-688">
                {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-689">
                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm'}`} data-id="element-690">
                      {msg.role === 'assistant' && <div className="flex items-center gap-1.5 mb-1.5 text-indigo-600" data-id="element-691">
                          <Sparkles size={12} data-id="element-692" />
                          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-693">
                            Bilton AI
                          </span>
                        </div>}
                      <div className="whitespace-pre-wrap leading-relaxed" data-id="element-694">
                        {msg.text}
                      </div>
                    </div>
                  </div>)}

                {isTyping && <div className="flex justify-start" data-id="element-695">
                    <div className="bg-white border border-gray-200 p-4 rounded-2xl rounded-tl-sm shadow-sm flex gap-1" data-id="element-696">
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" data-id="element-697" />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                    animationDelay: '0.2s'
                  }} data-id="element-698" />
                      <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                    animationDelay: '0.4s'
                  }} data-id="element-699" />
                    </div>
                  </div>}
                <div ref={messagesEndRef} data-id="element-700" />
              </div>

              {/* Chat Input */}
              <div className="p-4 bg-white border-t border-gray-200" data-id="element-701">
                {/* Quick Prompts */}
                <div className="flex flex-wrap gap-2 mb-3" data-id="element-702">
                  {quickPrompts.map((prompt, idx) => <button key={idx} onClick={() => handleSend(prompt)} className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full transition-colors whitespace-nowrap" data-id="element-703">
                      {prompt}
                    </button>)}
                </div>

                <div className="relative" data-id="element-704">
                  <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend(query)} placeholder="Ask about this insight..." className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" data-id="element-705" />
                  <button onClick={() => handleSend(query)} disabled={!query.trim() || isTyping} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors" data-id="element-706">
                    <Send size={16} data-id="element-707" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-708" />
    </AnimatePresence>;
}